import * as next from '../../../types/next'
import {
  PrefillContactEntityMode,
  PrefillContactSource
} from '../../../types/next'
import { CONTROL_NAME } from '../../../utils'
import type * as current from '../../../utils/types/sharedTypes'
import type { UpBlockHandler } from '../types'
import { findBlockIdWithStepId, newId } from '../utils'

import { upCommonSettings } from './common'

export const up: UpBlockHandler<next.ContactBlock> = (uischema, _, journey) => {
  if (uischema.type !== CONTROL_NAME.CONTACT_CONTROL) {
    throw new Error('Expected a valid ContactControl control')
  }

  const upField = (field: string) => ({
    display: uischema.options?.fields?.[field] ? true : false,
    required: uischema.options?.fields?.[field]?.required || false
  })

  const upPresetChoiceField = (field: string) => {
    const uiSchemaOptions = uischema.options

    const fieldOptions = upField(field)

    return {
      ...fieldOptions,
      // We only consider options when the field is displayed
      ...(fieldOptions.display && {
        options: uiSchemaOptions?.fields[field].options
      })
    }
  }

  const upPrefillSettings = (entityPrefill?: { mode: string }) => {
    if (!entityPrefill) {
      return undefined
    }

    return {
      source: PrefillContactSource.ENTITY,
      mode: PrefillContactEntityMode.AUTH_MODE
    }
  }

  return {
    type: next.BLOCK_TYPE.CONTACT,
    required: false, // can never be required
    settings: {
      ...upCommonSettings(uischema),
      purpose: uischema.options?.purposeLabels,
      prefillSettings: upPrefillSettings(uischema.options?.entityPrefill),
      fields: {
        salutation: upPresetChoiceField('salutation'),
        title: upField('title'),
        firstName: upField('firstName'),
        lastName: upField('lastName'),
        email: upField('email'),
        telephone: upField('telephone'),
        birthDate: upField('birthDate')
      }
    },
    references: upAutofillBlockReference(
      uischema.options?.isAutoFillPIEnabled,
      uischema.options?.autoFillPISettings,
      journey
    )
  }
}

function upAutofillBlockReference(
  isAutofillEnabled: boolean,
  settings: { relatedBlock: string },
  journey?: current.Journey
) {
  if (!isAutofillEnabled) {
    return {}
  }
  if (!journey) {
    throw new Error(
      'Journey is required to migrate contact block with references'
    )
  }

  const references: Record<next.ReferenceId, next.ContactBlockReference> = {}
  const [stepId, blockName] = settings.relatedBlock.split('/')
  const blockId = findBlockIdWithStepId(journey, stepId, blockName)

  if (!blockId) {
    // If the block is not found, skip creating the reference
    return references
  }
  const referenceId = newId<next.ReferenceId>()

  references[referenceId] = {
    meaning: 'autofill',
    referenceType: next.REFERENCED_TYPE.BLOCK,
    referenceId: blockId,
    id: referenceId
  }

  return references
}
